import { useCallback, useEffect, useState } from 'react';

import {
  getProjectDetails,
  addProjectLink,
  addTechStackItem,
  deleteProjectLink,
  deleteTechStackItem,
  type AddLinkRequest,
  type AddTechStackRequest,
} from '@/api/project-details/api';
import type { ProjectHomePageData } from '@/api/project-details/mocks/projectHome.types';
import { updateProject, type ResolveSharePointSharingLinkResponseDTO } from '@/api/projects/api';
import { createLogger } from '@/utils/logging';

export type { AddLinkRequest, AddTechStackRequest } from '@/api/project-details/api';
export type { ResolveSharePointSharingLinkResponseDTO } from '@/api/projects/api';
export type {
  ProjectHomePageData,
  PreviousWeeklyUpdateRecord,
} from '@/api/project-details/mocks/projectHome.types';

const logger = createLogger('useProjectHome', 'hook');

export interface UseProjectHomeResult {
  projectDetails: ProjectHomePageData | null;
  loading: boolean;
  addingLink: boolean;
  addingTechStack: boolean;
  techStackError: string | null;
  linkError: string | null;
  sharepointFolderWebUrl: string | null;
  loadProjectDetails: () => Promise<void>;
  handleAddLink: (linkData: AddLinkRequest) => Promise<void>;
  handleAddTechStack: (techData: AddTechStackRequest) => Promise<void>;
  handleDeleteLink: (linkId: string) => Promise<void>;
  handleDeleteTechStack: (techId: string) => Promise<void>;
  handleSaveSharePoint: (resolved: ResolveSharePointSharingLinkResponseDTO) => Promise<void>;
  clearTechStackError: () => void;
  clearLinkError: () => void;
}

/**
 * Orchestrates all data fetching and mutations for the project home page.
 * Optimistically updates local state on successful mutations to avoid a full refetch.
 */
export function useProjectHome(
  accessToken: string,
  clientId: string,
  projectId: string,
): UseProjectHomeResult {
  const [projectDetails, setProjectDetails] = useState<ProjectHomePageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [addingLink, setAddingLink] = useState(false);
  const [addingTechStack, setAddingTechStack] = useState(false);
  const [techStackError, setTechStackError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const loadProjectDetails = useCallback(async () => {
    try {
      setLoading(true);
      const details = await getProjectDetails(
        accessToken,
        clientId,
        projectId,
        'metadata,team,health,trends,audit,updates',
      );
      setProjectDetails(details);
    } catch (error) {
      logger.error('Failed to load project details:', error);
    } finally {
      setLoading(false);
    }
  }, [accessToken, clientId, projectId]);

  useEffect(() => {
    if (accessToken && projectId) {
      void loadProjectDetails();
    }
  }, [projectId, accessToken, loadProjectDetails]);

  const handleAddLink = useCallback(
    async (linkData: AddLinkRequest): Promise<void> => {
      setAddingLink(true);
      setLinkError(null);
      try {
        const createdLink = await addProjectLink(accessToken, clientId, projectId, linkData);
        setProjectDetails((current) => {
          if (!current) {
            return current;
          }
          return { ...current, projectLinks: [...(current.projectLinks ?? []), createdLink] };
        });
      } catch (error) {
        logger.error('Failed to add project link:', error);
        if (error instanceof Response && error.status === 403) {
          setLinkError('You do not have permission to manage project links.');
        } else if (error instanceof Error) {
          setLinkError(error.message);
        } else {
          setLinkError('Failed to add link');
        }
        throw error;
      } finally {
        setAddingLink(false);
      }
    },
    [accessToken, clientId, projectId],
  );

  const handleAddTechStack = useCallback(
    async (techData: AddTechStackRequest): Promise<void> => {
      setAddingTechStack(true);
      setTechStackError(null);
      try {
        const createdItem = await addTechStackItem(accessToken, clientId, projectId, techData);
        setProjectDetails((current) => {
          if (!current) {
            return current;
          }
          return { ...current, techStackItems: [...(current.techStackItems ?? []), createdItem] };
        });
      } catch (error) {
        logger.error('Failed to add tech stack item:', error);
        if (error instanceof Response && error.status === 403) {
          setTechStackError('You do not have permission to manage the tech stack.');
        } else if (error instanceof Error) {
          setTechStackError(error.message);
        } else {
          setTechStackError('Failed to add technology');
        }
        throw error;
      } finally {
        setAddingTechStack(false);
      }
    },
    [accessToken, clientId, projectId],
  );

  const handleDeleteLink = useCallback(
    async (linkId: string): Promise<void> => {
      setLinkError(null);
      try {
        await deleteProjectLink(accessToken, clientId, projectId, linkId);
        setProjectDetails((current) => {
          if (!current) {
            return current;
          }
          return {
            ...current,
            projectLinks: (current.projectLinks ?? []).filter((link) => link.id !== linkId),
          };
        });
      } catch (error) {
        logger.error('Failed to delete project link:', error);
        if (error instanceof Response && error.status === 403) {
          setLinkError('You do not have permission to manage project links.');
        } else if (error instanceof Error) {
          setLinkError(error.message);
        } else {
          setLinkError('Failed to delete link');
        }
        throw error;
      }
    },
    [accessToken, clientId, projectId],
  );

  const handleDeleteTechStack = useCallback(
    async (techId: string): Promise<void> => {
      setTechStackError(null);
      try {
        await deleteTechStackItem(accessToken, clientId, projectId, techId);
        setProjectDetails((current) => {
          if (!current) {
            return current;
          }
          return {
            ...current,
            techStackItems: (current.techStackItems ?? []).filter((item) => item.id !== techId),
          };
        });
      } catch (error) {
        logger.error('Failed to delete tech stack item:', error);
        if (error instanceof Response && error.status === 403) {
          setTechStackError('You do not have permission to manage the tech stack.');
        } else if (error instanceof Error) {
          setTechStackError(error.message);
        } else {
          setTechStackError('Failed to delete technology');
        }
        throw error;
      }
    },
    [accessToken, clientId, projectId],
  );

  const handleSaveSharePoint = useCallback(
    async (resolved: ResolveSharePointSharingLinkResponseDTO): Promise<void> => {
      try {
        await updateProject(accessToken, clientId, projectId, {
          sharepoint_site_id: resolved.sharepoint_site_id,
          sharepoint_drive_id: resolved.sharepoint_drive_id,
          sharepoint_folder_item_id: resolved.sharepoint_folder_item_id,
          sharepoint_folder_web_url: resolved.sharepoint_folder_web_url,
        });
        setProjectDetails((current) => {
          if (!current) {
            return current;
          }
          return { ...current, sharepointFolderWebUrl: resolved.sharepoint_folder_web_url };
        });
      } catch (error) {
        logger.error('Failed to save SharePoint folder:', error);
        throw error;
      }
    },
    [accessToken, clientId, projectId],
  );

  const clearTechStackError = useCallback(() => {
    setTechStackError(null);
  }, []);

  const clearLinkError = useCallback(() => {
    setLinkError(null);
  }, []);

  return {
    projectDetails,
    loading,
    addingLink,
    addingTechStack,
    techStackError,
    linkError,
    sharepointFolderWebUrl: projectDetails?.sharepointFolderWebUrl ?? null,
    loadProjectDetails,
    handleAddLink,
    handleAddTechStack,
    handleDeleteLink,
    handleDeleteTechStack,
    handleSaveSharePoint,
    clearTechStackError,
    clearLinkError,
  };
}
